import { Injectable } from '@angular/core';
import { debounce, Throttle } from 'lodash-decorators';
import { Observable, Subject } from 'rxjs';
import { oneMinuteMilli } from '../../constants-types/constants';
import { RoleType } from '../../constants-types/Enums';
import { AccountStatus } from '../../core-data-model/InitData.model';
import { InterviewGuideReminder } from '../../core-data-model/InterviewGuideReminder.model';
import { JobStub } from '../../core-data-model/Job/JobStub.model';
import { OiLink } from '../../core-data-model/OiLink.model';
import {
    OrderedSet,
    StandardMaxOrderedSetSize
} from '../../core-data-model/OrderedSet';
import {
    CompanySpecificSetting,
    HireSpecificSettings,
    OnboardingChecklist,
    OnboardingNextStepItemState,
    OnboardingState,
    UserProfileSetting
} from '../../core-data-model/UserProfileSetting.model';
import { InitDataService } from '../../system-services/InitData/InitData.service';
import { UserProfileSettingApi } from '../../system-services/UserProfileSettings/UserProfileSetting.connect';
import { UserProfileSettingTransformer } from '../../system-services/UserProfileSettings/UserProfileSetting.datatransform';

// TODO Consider Merging UserProfile and InitData since they are becomgin very overlapped

@Injectable({ providedIn: 'root' })
export class UserProfileSettingsService {
    userProfileSetting: UserProfileSetting;
    companySpecificSetting: CompanySpecificSetting;
    public userProfileSetting$: Observable<UserProfileSetting>;

    // Store this as a point of reference for the current company because
    // this value will never change. Fixes PI-8130 which is caused by the current
    // company flipping for a second before page reload when selecting a diff.
    // company in the company dropdown.
    private currentCompanyId: string;
    private userProfileSettingSubject: Subject<UserProfileSetting>;

    constructor(
        private userProfileApi: UserProfileSettingApi,
        private userProfileTransformer: UserProfileSettingTransformer,
        private initData: InitDataService
    ) {}

    get onboardingChecklistDoneState(): 'notDone' | 'addUsersSkipped' | 'done' {
        if (
            this.onboardingChecklist.initialLogin === true &&
            this.onboardingChecklist.connectBehavioral === true &&
            this.onboardingChecklist.inviteEmployeesBA === true &&
            this.onboardingChecklist.addMoreUsers === 'done'
        ) {
            return 'done';
        } else if (
            this.onboardingChecklist.initialLogin === true &&
            this.onboardingChecklist.connectBehavioral === true &&
            this.onboardingChecklist.addMoreUsers === 'skipped'
        ) {
            return 'addUsersSkipped';
        } else {
            return 'notDone';
        }
    }
    get hireSettings(): HireSpecificSettings {
        return this.userProfileSetting.hireSettings;
    }

    set hireSettings(value) {
        this.userProfileSetting.hireSettings = value;
        this.setUserProfileSetting();
    }

    get currentCompany(): string {
        return this.currentCompanyId;
    }

    get myUserId(): string {
        return this.initData.user.userGuid;
    }

    // This feels a little too boilerplatey to me
    // NOTE: Use this to hide/unhide cognitive stuff
    get showCognitive(): boolean {
        // Users switch companies so the isCognitiveEnabled should take precedence
        return (
            this.userProfileSetting &&
            this.userProfileSetting.showCognitiveData &&
            this.isCognitiveEnabled()
        );
    }

    set showCognitive(value) {
        this.userProfileSetting.showCognitiveData = value;
        this.setUserProfileSetting();
    }

    get hasAcknowledgedWelcomeGuideCta(): boolean {
        return this.userProfileSetting?.hasAcknowledgedWelcomeGuideCta;
    }
    set hasAcknowledgedWelcomeGuideCta(value: boolean) {
        this.userProfileSetting.hasAcknowledgedWelcomeGuideCta = value;
        this.setUserProfileSetting();
    }

    get interviewReminderLastShown(): Date {
        return this.userProfileSetting.interviewReminderLastShown;
    }
    set interviewReminderLastShown(value: Date) {
        this.userProfileSetting.interviewReminderLastShown = value;
        this.setUserProfileSetting();
    }

    get completedCardAdministeredByMe(): boolean {
        return (
            this.userProfileSetting &&
            // Explicit true check so that undefined is treated as false
            this.userProfileSetting.completedCardAdministeredByMe === true
        );
    }
    set completedCardAdministeredByMe(value) {
        this.userProfileSetting.completedCardAdministeredByMe = value;
        this.setUserProfileSetting();
    }

    get onboarding(): OnboardingState {
        if (!this.userProfileSetting) {
            return 'unstarted';
        }
        return this.userProfileSetting.onboarding;
    }
    set onboarding(value: OnboardingState) {
        this.userProfileSetting.onboarding = value;
        this.setUserProfileSetting();
    }

    get jobCardModifiedByMe(): boolean {
        return (
            this.userProfileSetting &&
            this.userProfileSetting.jobCardModifiedByMe
        );
    }
    set jobCardModifiedByMe(value) {
        this.userProfileSetting.jobCardModifiedByMe = value;
        this.setUserProfileSetting();
    }

    get preferredPageSize(): number {
        return (
            this.userProfileSetting && this.userProfileSetting.preferredPageSize
        );
    }

    set preferredPageSize(value) {
        this.userProfileSetting.preferredPageSize = value;
        this.setUserProfileSetting();
    }

    get pendingCardAdministeredByMe(): boolean {
        return (
            this.userProfileSetting &&
            // Explicit true check so that undefined is treated as false
            this.userProfileSetting.pendingCardAdministeredByMe === true
        );
    }
    set pendingCardAdministeredByMe(value) {
        this.userProfileSetting.pendingCardAdministeredByMe = value;
        this.setUserProfileSetting();
    }

    get myOiLink(): OiLink {
        return this.userProfileSetting && this.userProfileSetting.myOiLink;
    }
    set myOiLink(value) {
        this.userProfileSetting.myOiLink = value;
        this.setUserProfileSetting();
    }

    get reminders(): InterviewGuideReminder[] {
        return (
            (this.userProfileSetting && this.userProfileSetting.reminders) || []
        );
    }

    get onboardingChecklist(): OnboardingChecklist {
        this.userProfileSetting.initOnboardingChecklist();
        return this.userProfileSetting.onboardingChecklist;
    }
    set onboardingChecklist(value) {
        this.userProfileSetting.onboardingChecklist = value;
        this.setUserProfileSetting();
    }
    get onboardingNextSteps(): OnboardingNextStepItemState {
        if (!this.userProfileSetting.onboardingNextSteps) {
            this.onboardingNextSteps = {
                personSnapshot: false,
                personalPlacard: false,
                pdc: false,
                relationshipGuide: false,
                teamPlacard: false,
                teamWorkstyles: false
            };
        }
        return this.userProfileSetting.onboardingNextSteps;
    }
    set onboardingNextSteps(value) {
        this.userProfileSetting.onboardingNextSteps = value;
        this.setUserProfileSetting();
    }

    @debounce(250)
    updateMostRecentJobs(jobs: JobStub[]) {
        this.companySpecificSetting.recentlySelectedJobs.orderedArray = jobs;
        this.userProfileSetting.companySpecificSettings.updateArrayTop(
            this.companySpecificSetting,
            this.areSameCompany
        );
        this.setUserProfileSetting();
    }

    @Throttle(5 * oneMinuteMilli, { leading: true }) // allow firing at beginning of every 5 minute period
    private throttledSetUserProfileSetting(): Promise<any> | any {
        return this.internalSetUserProfileSetting();
    }

    setup() {
        this.userProfileSettingSubject = new Subject<UserProfileSetting>();
        this.userProfileSetting$ =
            this.userProfileSettingSubject.asObservable();

        const isFirstLogin =
            !this.initData.javaScriptInitData.userProfile.settings;
        this.userProfileSetting = this.userProfileTransformer.serverToClient(
            this.initData.javaScriptInitData.userProfile.settings
        );
        this.currentCompanyId = this.userProfileSetting.currentCompany;
        if (isFirstLogin) {
            this.userProfileSetting.firstLoginConstruction();
            this.setUserProfileSetting();
        }
        if (!this.userProfileSetting.currentCompany) {
            this.userProfileSetting.currentCompany =
                this.initData.user.companyId;
        }
        this.userProfileSettingSubject.next(this.userProfileSetting);

        this.companySpecificSetting = this.getCurrentCompanySettings();
        this.userProfileSetting.hasAcknowledgedCogBestPractices =
            this.initData.user.hasAcknowledgedCogBestPractices;

        if (
            this.initData.account.accountStatus === AccountStatus.Prospect &&
            this.userProfileSetting.onboarding === 'unstarted'
        ) {
            this.userProfileSetting.onboarding = 'finished';
        }
    }

    getCurrentCompanySettings(): CompanySpecificSetting {
        let result;
        if (
            this.userProfileSetting.companySpecificSettings.orderedArray
                .length > 0
        ) {
            result =
                this.userProfileSetting.companySpecificSettings.orderedArray.find(
                    (setting) =>
                        setting.companyId ===
                        this.userProfileSetting.currentCompany
                );
        }
        return (
            result ||
            new CompanySpecificSetting({
                companyId: this.userProfileSetting.currentCompany,
                recentlySelectedFolderIds: new OrderedSet<string>(
                    [],
                    StandardMaxOrderedSetSize + 1,
                    StandardMaxOrderedSetSize
                ),
                recentlySelectedGroupIds: new OrderedSet<string>(
                    [],
                    StandardMaxOrderedSetSize + 1,
                    StandardMaxOrderedSetSize
                ),
                recentlySelectedJobs: new OrderedSet<JobStub>(
                    [],
                    StandardMaxOrderedSetSize + 1,
                    StandardMaxOrderedSetSize
                )
            })
        );
    }

    setUserProfileSetting(force = false): Promise<any> | any {
        return force
            ? this.internalSetUserProfileSetting()
            : this.throttledSetUserProfileSetting();
    }

    updateMostRecentFolders(folders: string[]) {
        this.companySpecificSetting.recentlySelectedFolderIds.orderedArray =
            folders;
        this.userProfileSetting.companySpecificSettings.updateArrayTop(
            this.companySpecificSetting,
            this.areSameCompany
        );
        this.setUserProfileSetting();
    }

    updateMostRecentGroups(groups: string[]) {
        this.companySpecificSetting.recentlySelectedGroupIds.orderedArray =
            groups;
        this.userProfileSetting.companySpecificSettings.updateArrayTop(
            this.companySpecificSetting,
            this.areSameCompany
        );
        this.setUserProfileSetting();
    }

    setCurrentCompany(company: string): Promise<void> {
        this.userProfileSetting.currentCompany = company;
        return this.setUserProfileSetting(true);
    }

    areSameCompany(
        companySettings1: CompanySpecificSetting,
        companySettings2: CompanySpecificSetting
    ) {
        return companySettings1.companyId === companySettings2.companyId;
    }

    setTableSettings(tableId: string, propertyName: string, active: boolean) {
        this.userProfileSetting.tableColumnSettings =
            this.userProfileSetting.tableColumnSettings || {};
        this.userProfileSetting.tableColumnSettings[tableId] =
            this.userProfileSetting.tableColumnSettings[tableId] || {};
        this.userProfileSetting.tableColumnSettings[tableId][propertyName] =
            active;
        return this.setUserProfileSetting(true);
    }
    getTableSettings(tableId: string) {
        const tableSettings = this.userProfileSetting.tableColumnSettings || {};
        return tableSettings[tableId] ? tableSettings[tableId] : {};
    }

    // NOTE: This handles showing whether a user has cognitive access or not for Third Party Users[Partners]
    // Requirement was part of Duality Initiative
    // https://predictiveindex.atlassian.net/browse/PI2-600
    showCognitiveAccessForDualityONLY(): boolean {
        if (this.isUserThirdPartyOrHasSuperAdminAccess()) {
            return this.initData.account.hasCognitiveAccessCompanyLevel;
        }
        return this.showCognitive;
    }

    getDefaultFolderId() {
        return this.initData.javaScriptInitData.userProfile.defaultFolderGuid;
    }

    addReminder(reminder: InterviewGuideReminder) {
        if (!this.userProfileSetting.reminders) {
            this.userProfileSetting.reminders = [];
        }
        const existingReminder = this.userProfileSetting.reminders.find(
            (possibleReminder) =>
                possibleReminder.personId === reminder.personId &&
                possibleReminder.companyId === reminder.companyId &&
                possibleReminder.jobId === reminder.jobId
        );
        if (existingReminder) {
            existingReminder.date = new Date();
        } else {
            this.userProfileSetting.reminders.push(reminder);
        }
        this.setUserProfileSetting();
    }

    isCognitiveEnabled() {
        // Override all cog settings for headquarters
        if (this.hideCognitiveInProductionHeadquarters()) {
            return false;
        }
        let output = false;

        // Consider Cognitive Access only when user is logged in his own Company or is isSuperAdmin
        // Also AccountAdmin gets automatic access to cognitive in his own Company
        // NOTE: P.O confirmed the behavior as requirements for now and plan to update it soon once the
        // core will be able to have User-Company Level Cognitive Access
        if (this.initData.account.hasCognitiveAccessCompanyLevel) {
            output =
                this.initData.user.isSuperAdmin() ||
                (this.initData.user.isAcccountOwner() &&
                    this.currentCompany === this.initData.user.companyId) ||
                (this.initData.user.hasCognitiveAccessUserLevel &&
                    this.currentCompany === this.initData.user.companyId);
        }
        return output;
    }

    isUserLoggedInDefaultCompany(): boolean {
        return (
            this.userProfileSetting.currentCompany ===
            this.initData.user.companyId
        );
    }

    isUserThirdPartyOrHasSuperAdminAccess(): boolean {
        return (
            !this.isUserLoggedInDefaultCompany() ||
            this.initData.hasPermissionLevelOrAbove(RoleType.SuperAdmin)
        );
    }

    // A separate method required where it does not check for third party access
    // as they have completely different permissions
    isUserSuperAdmin(): boolean {
        return this.initData.hasPermissionLevelOrAbove(RoleType.SuperAdmin);
    }

    getPresumedStartDate(): Date {
        return (
            this.userProfileSetting.firstLogin || this.initData.user.createdTime
        );
    }

    inMyOwnCompany(): boolean {
        return this.currentCompany === this.initData.user.companyId;
    }

    setHireSettings(settings: HireSpecificSettings): Promise<any> {
        this.userProfileSetting.hireSettings = settings;
        return this.setUserProfileSetting(true);
    }

    private internalSetUserProfileSetting(): Promise<any> | any {
        return this.userProfileApi
            .setProfileSettings(this.userProfileSetting)
            .catch(() => {
                // Server failed to update the Settings. Maintaing the previously known synced state.
                this.userProfileApi
                    .getProfileSettings()
                    .then((setting) => (this.userProfileSetting = setting));
            })
            .finally(() => {
                this.userProfileSettingSubject.next(this.userProfileSetting);
            });
    }

    private hideCognitiveInProductionHeadquarters(): boolean {
        const ignoredUsers = [
            'wotto@predictiveindex.com',
            'jdube@predictiveindex.com',
            'krichardson@predictiveindex.com',
            'ewillbrant@predictiveindex.com',
            'dvalente@predictiveindex.com',
            'jcross@predictiveindex.com',
            'clopes@predictiveindex.com'
        ];
        const isIgnoredUser = ignoredUsers.includes(this.initData.user.email);
        const isPIHeadquarters =
            this.currentCompany === 'dda0ef04-4309-42c3-8acb-2e90e622d390';
        // For DEV: '507e0ad8-a595-4b05-8801-34284370f8b0'
        return !isIgnoredUser && isPIHeadquarters;
    }
}
